import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import {
  createUpdateContractorCitationForm,
  CreateUpdateContractorCitationFormGroup
} from '@umss/shared/utils';
import { UmssDialogHeaderComponent } from '../dialog-header/dialog-header.component';
import { UmssDialogFooterComponent } from '../dialog-footer/dialog-footer.component';
import { UmssVerticalFieldComponent } from '../vertical-field/vertical-field.component';
import { UmssInputComponent } from '../input/input.component';
import { UmssTextareaComponent } from '../textarea/textarea.component';
import {
  ContractorCitationDetailDto,
  ContractorCitationService
} from '@umss/shared/data-access';
import { UmssCitationStatusBadgeComponent } from '../citation-status-badge/citation-status-badge.component';
import { UmssAlertDialogComponent } from '../alert-dialog/alert-dialog.component';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'umss-contractor-citation-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DatePipe,
    UmssDialogHeaderComponent,
    UmssDialogFooterComponent,
    UmssVerticalFieldComponent,
    UmssInputComponent,
    UmssTextareaComponent,
    UmssCitationStatusBadgeComponent,
    UmssAlertDialogComponent
  ],
  templateUrl: './contractor-citation-details-dialog.component.html',
  styleUrls: ['./contractor-citation-details-dialog.component.scss']
})
export class ContractorCitationDetailsDialogComponent implements OnChanges {
  @Input({ required: true }) citationId!: string;
  @Input({ required: true }) mode: 'view' | 'edit' = 'view';
  @Input() allowResolve = false;
  @Output() closeDialog = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();
  @Output() resolved = new EventEmitter<void>();

  private readonly citationService = inject(ContractorCitationService);
  private readonly toastService = inject(ToastService);

  citation: ContractorCitationDetailDto | null = null;
  isLoading = false;
  form: CreateUpdateContractorCitationFormGroup | null = null;
  showResolveConfirmDialog = false;
  get showResolveButton(): boolean {
    return this.allowResolve && this.citation?.status !== 'Resolved';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['citationId'] && this.citationId) this.loadCitation();

    if (changes['mode']) {
      switch (this.mode) {
        case 'view':
          this.form = null;
          break;
        case 'edit':
          this.form = createUpdateContractorCitationForm();
          break;
      }
    }
  }

  private loadCitation(): void {
    this.isLoading = true;

    this.citationService.get(this.citationId).subscribe({
      next: (detail) => {
        this.citation = detail;
        this.updateFormValues();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading citation details:', error);
        this.isLoading = false;
      }
    });
  }

  private updateFormValues(): void {
    if (!this.citation || !this.form) return;
    this.form.patchValue(this.citation);
  }

  onClose(): void {
    this.closeDialog.emit();
  }

  onCancel(): void {
    this.closeDialog.emit();
  }

  onSave(): void {
    if (!this.citation || !this.form) return;
    const value = this.form.getRawValue();

    this.citationService.update(this.citation.id, value).subscribe({
      next: () => {
        this.saved.emit();

        this.toastService.openToast({
          type: 'Success',
          title: 'Citation updated successfully'
        });
      },
      error: (error) => {
        console.error('Error updating citation:', error);

        this.toastService.openToast({
          type: 'Error',
          title: error?.error?.message || 'Failed to update citation'
        });
      }
    });
  }

  onMarkAsResolved(): void {
    this.showResolveConfirmDialog = true;
  }

  confirmResolve(): void {
    if (!this.citation) return;
    this.showResolveConfirmDialog = false;
    this.citationService.resolve(this.citation.id).subscribe({
      next: () => {
        this.resolved.emit();
        this.toastService.openToast({
          type: 'Success',
          title: 'Citation resolved successfully'
        });
      },
      error: (error) => {
        console.error('Error resolving citation:', error);

        this.toastService.openToast({
          type: 'Error',
          title: error?.error?.message || 'Failed to resolve citation'
        });
      }
    });
  }

  cancelResolve(): void {
    this.showResolveConfirmDialog = false;
  }
}
